import React from "react";
import { Box, Chip, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { formatCapturedAt, tableauDisplayTitle } from "./displayHelpers";
import {
  accentColor,
  chipQuietSx,
  isDarkMode,
  surfaceSx,
} from "./resultatsTheme";
import type { ResultsDoc } from "./types";

interface TableauMetaProps {
  doc: ResultsDoc;
  boards?: { name: string; label: string }[];
  tournoiLabel?: string | null;
}

export default function TableauMeta({
  doc,
  boards = [],
  tournoiLabel,
}: TableauMetaProps): React.ReactElement {
  const theme = useTheme();
  const isDark = isDarkMode(theme);
  const captured = formatCapturedAt(doc.sourceCapturedAt);
  const completed = doc.summary?.completed;
  const total =
    doc.summary != null
      ? doc.summary.poolMatches + doc.summary.knockoutMatches
      : null;
  const title = tableauDisplayTitle(doc, boards);
  const tournoiName = tournoiLabel?.trim() || null;
  const qualified =
    doc.summary?.qualifiedCount ??
    (doc.pools.some((p) =>
      p.standings.some((s) => s.qualification === "QUALIFIED")
    )
      ? doc.pools.reduce(
          (acc, pool) =>
            acc +
            pool.standings.filter((s) => s.qualification === "QUALIFIED")
              .length,
          0
        )
      : null);
  const playerCount = doc.playerCount ?? doc.participants?.length ?? null;

  return (
    <Box component="header" sx={{ ...surfaceSx(theme), p: { xs: 2, sm: 2.75 } }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.75}
        alignItems={{ sm: "flex-start" }}
        justifyContent="space-between"
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="overline"
            sx={{
              display: "block",
              letterSpacing: "0.12em",
              fontWeight: 700,
              color: accentColor(theme),
              mb: 0.35,
              lineHeight: 1.2,
            }}
          >
            Tableau
          </Typography>
          <Typography
            variant="h5"
            component="h2"
            sx={{
              fontWeight: 800,
              fontSize: { xs: "1.35rem", sm: "1.6rem" },
              letterSpacing: "-0.02em",
              lineHeight: 1.15,
              mb: 0.5,
            }}
          >
            {title}
          </Typography>
          {tournoiName && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: captured ? 0.65 : 0 }}
            >
              {tournoiName}
            </Typography>
          )}
          {captured && (
            <Stack direction="row" spacing={0.75} alignItems="center">
              <AccessTimeIcon sx={{ fontSize: 15, color: "text.secondary" }} />
              <Typography variant="caption" color="text.secondary">
                Mis à jour le {captured}
              </Typography>
            </Stack>
          )}
        </Box>

        <Stack
          direction="row"
          spacing={0.75}
          flexWrap="wrap"
          useFlexGap
          sx={{ maxWidth: { sm: 320 }, justifyContent: { sm: "flex-end" } }}
        >
          {playerCount != null && (
            <Chip
              size="small"
              variant="outlined"
              label={`${playerCount} joueur${playerCount > 1 ? "s" : ""}`}
              sx={chipQuietSx(theme)}
            />
          )}
          {doc.pools.length > 0 && (
            <Chip
              size="small"
              variant="outlined"
              label={`${doc.pools.length} poule${doc.pools.length > 1 ? "s" : ""}`}
              sx={chipQuietSx(theme)}
            />
          )}
          {qualified != null && qualified > 0 && (
            <Chip
              size="small"
              color="success"
              variant={isDark ? "filled" : "outlined"}
              label={`${qualified} qualifié${qualified > 1 ? "s" : ""}`}
              sx={{ fontWeight: 700 }}
            />
          )}
          {completed != null && total != null && (
            <Chip
              size="small"
              color="secondary"
              variant={isDark ? "filled" : "outlined"}
              label={`${completed}/${total} parties`}
              sx={
                isDark
                  ? { color: "grey.900", fontWeight: 700 }
                  : { fontWeight: 700 }
              }
            />
          )}
        </Stack>
      </Stack>
    </Box>
  );
}
